import { SocketContext } from "@contexts/SocketContext";
import SupplierContext from "@contexts/SupplierContext";
import UiContext from "@contexts/UiContext";
import UserContext from "@contexts/UserContext";
import IGetCatalogsDTO from "@models/DTO/IGetCatalogsDTO";
import IOnGetCatalogsDTO from "@models/DTO/IOnGetCatalogsDTO";
import IUpdateSupplierIntegrationsDTO from "@models/DTO/IUpdateSupplierIntegrationsDTO";
import EndpointsEnum from "@models/EndpointsEnum";
import ICatalog from "@models/ICatalog";
import INotification from "@models/INotification";
import ISupplier_Integration from "@models/ISupplier_Integration";
import IUiSocketSetup from "@models/IUiSocketSetup";
import NotificationLevelEnum from "@models/NotificationLevelEnum";
import NotificationPositionEnum from "@models/NotificationPositionEnum";
import QBDCompanyFieldForOWCompanyNameEnum from "@models/QBDCompanyFieldForOWCompanyNameEnum";
import QBDSkuPropertyEnum from "@models/QBDSkuPropertyEnum";
import IQBDSubtotalOption from "@models/QuickBooks Desktop/IQBDSubtotalOption";
import UIService from "@services/UIService";
import { ChangeEvent, useContext, useEffect, useState } from "react";
import quickbooksDesktopLogo from "@assets/images/quickbooks/quickbooks-desktop-logo-white.png";
import quickbooksDesktopLightLogo from "@assets/images/quickbooks/quickbooks-desktop-logo.png";
import { OwAccordion } from "@components/shared/OwAccordion/OwAccordion";
import OwButton from "@components/shared/OwButton/OwButton";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import OwPill from "@components/shared/OwPill/OwPill";
import OwToggle from "@components/shared/OwToggle/OwToggle";
import { QuickbooksDesktopConfigCustomersSection } from "../QuickbooksDesktopConnect/QuickbooksDesktopConfigCustomersSection/QuickbooksDesktopConfigCustomersSection";
import { QuickbooksDesktopConfigInvoicesSection } from "../QuickbooksDesktopConnect/QuickbooksDesktopConfigInvoicesSection/QuickbooksDesktopConfigInvoicesSection";
import { QuickbooksDesktopConfigItemsSection } from "../QuickbooksDesktopConnect/QuickbooksDesktopConfigItemsSection/QuickbooksDesktopConfigItemsSection";
import { QuickbooksDesktopConfigOrdersSection } from "../QuickbooksDesktopConnect/QuickbooksDesktopConfigOrdersSection/QuickbooksDesktopConfigOrdersSection";
import { QuickbooksDesktopConfigOtherSection } from "../QuickbooksDesktopConnect/QuickbooksDesktopConfigOtherSection/QuickbooksDesktopConfigOtherSection";
import { QuickbooksDesktopConfigSalesOrdersSection } from "../QuickbooksDesktopConnect/QuickbooksDesktopConfigSalesOrdersSection/QuickbooksDesktopConfigSalesOrdersSection";
import { QuickbooksDesktopConfigSetup } from "../QuickbooksDesktopConnect/QuickbooksDesktopConfigSetup/QuickbooksDesktopConfigSetup";
import { QuickbooksDesktopQueue } from "../QuickbooksDesktopConnect/QuickbooksDesktopQueue/QuickbooksDesktopQueue";
import IOnUpdateSupplierIntegrationsDTO from "@models/DTO/IOnUpdateSupplierIntegrationsDTO";
import OwTitle from "@components/shared/OwTitle/OwTitle";

type Props = {};

interface IQBDIntegrationProps {
    qbd_AllowCreateCustomers?: boolean;
    qbd_AllowCreateItems?: boolean;
    qbd_AllowUpdateCustomers?: boolean;
    qbd_AllowUpdateItems?: boolean;
    qbd_AutoAssignAccountsToRepGroup: boolean;
    qbd_AutoPullAndProcessCatalogItems?: boolean;
    qbd_AutoPullAndProcessCustomers?: boolean;
    qbd_CatItemSkuProperty?: QBDSkuPropertyEnum;
    qbd_CompanyFieldForOwCompanyName: QBDCompanyFieldForOWCompanyNameEnum;
    qbd_CompanyFilePath: string;
    qbd_CreateRepGroupsFromRepDetails: boolean;
    qbd_DefaultAssetAccount: string;
    qbd_DefaultCOGSAccount: string;
    qbd_DefaultIncomeAccount: string;
    qbd_DefaultTaxRate: string;
    qbd_DefaultTerm: string;
    qbd_Invoice_TemplateRef: string;
    qbd_NoPriceLevelCatalogId?: number;
    qbd_NoPriceLevelPriceGroupId?: number;
    qbd_PriceToCatalogId?: number;
    qbd_PriceToPriceGroupId?: number;
    qbd_SO_TemplateRef: string;
    qbd_UseOWOrderNoForSO: boolean;
    qbd_UseOWOrderNoForInvoice: boolean;
    qbd_address_shift?: boolean;
    qbd_allow_straight_invoice: boolean;
    qbd_grouped_bottom_desc: string;
    qbd_grouped_subtotal_item: IQBDSubtotalOption | null;
    qbd_has_grouped_invoice: boolean;
    qbd_has_price_levels: boolean;
    qbd_invoice_use_order_ship_to?: boolean;
    qbd_sales_order_use_order_ship_to?: boolean;
    qbd_append_shipping_instructions_to_memo?: boolean;
    qbd_shipping_catalog_id?: number;
    qbd_shipping_catalog_item_id?: number;
    qbd_show_ship_via_options?: boolean;
    qbd_skip_product_group_creation?: boolean;
    qbd_use_mfg_sku_for_mfg_pt_no?: boolean;
    qbd_use_queue_table: boolean;
    qbd_ignore_qty_on_hand: boolean;
    qbd_version: string;
    qbd_year: string;
}

export const QuickbooksDesktopIntegration = (props: Props) => {
    const uiContext = useContext(UiContext);
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const socket = useContext(SocketContext)!;

    const [catalogs, setCatalogs] = useState<ICatalog[]>([]);
    const [isDirty, setIsDirty] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [supplierIntegration, setSupplierIntegration] = useState<IQBDIntegrationProps>({
        qbd_AutoAssignAccountsToRepGroup: supplierModel.Supplier_Integration.qbd_AutoAssignAccountsToRepGroup || false,
        qbd_CompanyFieldForOwCompanyName:
            supplierModel.Supplier_Integration.qbd_CompanyFieldForOwCompanyName || QBDCompanyFieldForOWCompanyNameEnum.COMPANY_NAME,
        qbd_CompanyFilePath: supplierModel.Supplier_Integration.qbd_CompanyFilePath || "",
        qbd_CreateRepGroupsFromRepDetails: supplierModel.Supplier_Integration.qbd_CreateRepGroupsFromRepDetails || false,
        qbd_DefaultAssetAccount: supplierModel.Supplier_Integration.qbd_DefaultAssetAccount || "",
        qbd_DefaultCOGSAccount: supplierModel.Supplier_Integration.qbd_DefaultCOGSAccount || "",
        qbd_DefaultIncomeAccount: supplierModel.Supplier_Integration.qbd_DefaultIncomeAccount || "",
        qbd_DefaultTaxRate: supplierModel.Supplier_Integration.qbd_DefaultTaxRate || "",
        qbd_DefaultTerm: supplierModel.Supplier_Integration.qbd_DefaultTerm || "",
        qbd_Invoice_TemplateRef: supplierModel.Supplier_Integration.qbd_Invoice_TemplateRef || "",
        qbd_SO_TemplateRef: supplierModel.Supplier_Integration.qbd_SO_TemplateRef || "",
        qbd_UseOWOrderNoForSO: supplierModel.Supplier_Integration.qbd_UseOWOrderNoForSO || false,
        qbd_allow_straight_invoice: supplierModel.Supplier_Integration.qbd_allow_straight_invoice || false,
        qbd_grouped_bottom_desc: supplierModel.Supplier_Integration.qbd_grouped_bottom_desc || "",
        qbd_grouped_subtotal_item: supplierModel.Supplier_Integration.qbd_grouped_subtotal_item || null,
        qbd_has_grouped_invoice: supplierModel.Supplier_Integration.qbd_has_grouped_invoice || false,
        qbd_has_price_levels: supplierModel.Supplier_Integration.qbd_has_price_levels || false,
        qbd_use_queue_table: supplierModel.Supplier_Integration.qbd_use_queue_table || false,
        qbd_version: supplierModel.Supplier_Integration.qbd_version || "",
        qbd_year: supplierModel.Supplier_Integration.qbd_year || "",
        qbd_ignore_qty_on_hand: supplierModel.Supplier_Integration.qbd_ignore_qty_on_hand || false,
        qbd_UseOWOrderNoForInvoice: supplierModel.Supplier_Integration.qbd_UseOWOrderNoForInvoice || false,
        qbd_append_shipping_instructions_to_memo: supplierModel.Supplier_Integration.qbd_append_shipping_instructions_to_memo || false,
    });

    const getCatalogs = () => {
        const payload: IGetCatalogsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            callMeta: {
                view: "QuickbooksDesktopConnect",
                fn: "getCatalogs",
            },
        };

        socket.emit(EndpointsEnum.CatalogsGetCatalogs, payload);
    };

    const onGetCatalogs = (data: IOnGetCatalogsDTO) => {
        setCatalogs(data.catalogs);
    };

    const onUpdateSupplierIntegrations = (data: IOnUpdateSupplierIntegrationsDTO) => {
        if (updating) {
            setUpdating(false);
            setIsDirty(false);
        }
    };

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.CatalogsOnGetCatalogs, fn: onGetCatalogs },
        { endpoint: EndpointsEnum.SupplierOnUpdateSupplierIntegrations, fn: onUpdateSupplierIntegrations },
    ];

    useEffect(() => {
        sockets.forEach((_socket) => {
            socket.on(_socket.endpoint, _socket.fn);
        });

        return () => {
            sockets.forEach((_socket) => {
                socket.off(_socket.endpoint, _socket.fn);
            });
        };
    }, [isDirty, updating]);

    useEffect(() => {
        sockets.forEach((_socket) => {
            socket.on(_socket.endpoint, _socket.fn);
        });

        return () => {
            sockets.forEach((_socket) => {
                socket.off(_socket.endpoint, _socket.fn);
            });
        };
    }, []);

    const toggleQuickBooksDesktop = (e: any) => {
        const name = e.target.name;
        const value = e.target.value;

        const payload: IUpdateSupplierIntegrationsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            // @ts-expect-error
            Supplier_Integration: {
                has_qbd: value,
            },
        };

        socket.emit(EndpointsEnum.SupplierUpdateSupplierIntegrations, payload);
    };

    const handleChange = (e: ChangeEvent<any>) => {
        const value = e.target.value;
        const name = e.target.name;

        const state: IQBDIntegrationProps = {
            ...supplierIntegration,
            [name]: value,
        };

        setIsDirty(true);
        setSupplierIntegration(state);
    };

    // do we still want to do it this way?
    const updateDefaults = () => {
        const payload: IUpdateSupplierIntegrationsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            Supplier_Integration: {
                qbd_DefaultTaxRate: supplierIntegration.qbd_DefaultTaxRate,
                qbd_DefaultTerm: supplierIntegration.qbd_DefaultTerm,
                qbd_DefaultIncomeAccount: supplierIntegration.qbd_DefaultIncomeAccount,
                qbd_DefaultAssetAccount: supplierIntegration.qbd_DefaultAssetAccount,
                qbd_DefaultCOGSAccount: supplierIntegration.qbd_DefaultCOGSAccount,
                qbd_SO_TemplateRef: supplierIntegration.qbd_SO_TemplateRef,
                qbd_Invoice_TemplateRef: supplierIntegration.qbd_Invoice_TemplateRef,
                qbd_has_grouped_invoice: supplierIntegration.qbd_has_grouped_invoice,
                qbd_grouped_subtotal_item: supplierIntegration.qbd_grouped_subtotal_item,
                qbd_grouped_bottom_desc: supplierIntegration.qbd_grouped_bottom_desc,
                qbd_allow_straight_invoice: supplierIntegration.qbd_allow_straight_invoice,
                qbd_shipping_catalog_id: supplierIntegration.qbd_shipping_catalog_id,
                qbd_shipping_catalog_item_id: supplierIntegration.qbd_shipping_catalog_item_id,
                qbd_has_price_levels: supplierIntegration.qbd_has_price_levels,
                qbd_CompanyFieldForOwCompanyName: supplierIntegration.qbd_CompanyFieldForOwCompanyName,
                qbd_PriceToCatalogId: supplierIntegration.qbd_PriceToCatalogId,
                qbd_PriceToPriceGroupId: supplierIntegration.qbd_PriceToPriceGroupId,
                qbd_CatItemSkuProperty: supplierIntegration.qbd_CatItemSkuProperty,
                qbd_AutoAssignAccountsToRepGroup: supplierIntegration.qbd_AutoAssignAccountsToRepGroup,
                qbd_CreateRepGroupsFromRepDetails: supplierIntegration.qbd_CreateRepGroupsFromRepDetails,
                qbd_NoPriceLevelCatalogId: supplierIntegration.qbd_NoPriceLevelCatalogId,
                qbd_NoPriceLevelPriceGroupId: supplierIntegration.qbd_NoPriceLevelPriceGroupId,
                qbd_AllowUpdateCustomers: supplierIntegration.qbd_AllowUpdateCustomers,
                qbd_AllowUpdateItems: supplierIntegration.qbd_AllowUpdateItems,
                qbd_UseOWOrderNoForSO: supplierIntegration.qbd_UseOWOrderNoForSO,
                qbd_UseOWOrderNoForInvoice: supplierIntegration.qbd_UseOWOrderNoForInvoice,
                qbd_AllowCreateItems: supplierIntegration.qbd_AllowCreateItems,
                qbd_AllowCreateCustomers: supplierIntegration.qbd_AllowCreateCustomers,
                qbd_use_mfg_sku_for_mfg_pt_no: supplierIntegration.qbd_use_mfg_sku_for_mfg_pt_no,
                qbd_invoice_use_order_ship_to: supplierIntegration.qbd_invoice_use_order_ship_to,
                qbd_sales_order_use_order_ship_to: supplierIntegration.qbd_sales_order_use_order_ship_to,
                qbd_append_shipping_instructions_to_memo: supplierIntegration.qbd_append_shipping_instructions_to_memo,
                qbd_address_shift: supplierIntegration.qbd_address_shift,
                qbd_skip_product_group_creation: supplierIntegration.qbd_skip_product_group_creation,
                qbd_show_ship_via_options: supplierIntegration.qbd_show_ship_via_options,
                qbd_AutoPullAndProcessCustomers: supplierIntegration.qbd_AutoPullAndProcessCustomers,
                qbd_AutoPullAndProcessCatalogItems: supplierIntegration.qbd_AutoPullAndProcessCatalogItems,
                qbd_use_queue_table: supplierIntegration.qbd_use_queue_table,
                qbd_ignore_qty_on_hand: supplierIntegration.qbd_ignore_qty_on_hand,
            } as ISupplier_Integration,
        };

        socket.emit(EndpointsEnum.SupplierUpdateSupplierIntegrations, payload);

        setUpdating(true);
    };

    let logo = quickbooksDesktopLogo;

    if (UIService.isMainViewLight()) {
        logo = quickbooksDesktopLightLogo;
    }

    const hasQbd = !!supplierModel.Supplier_Integration.has_qbd;

    const borderClass = UIService.isMainViewLight() ? "" : "text-bg-dark";

    return (
        <div className={"QuickbooksDesktopIntegration"}>
            <OwAccordion
                hideChevron={!hasQbd}
                leftImage={logo}
                leftImageHeight={50}
                leftImageAlt={"QuickBooks Desktop / Enterprise"}
                titleClick={true}
                rightElem={
                    <div className={"ms-auto"}>
                        <OwToggle
                            active={hasQbd}
                            name={"toggleQuickBooksDesktop"}
                            fn={toggleQuickBooksDesktop}
                            label={""}
                            showDisableConfirm={true}
                        />
                    </div>
                }>
                {hasQbd ? (
                    <div>
                        <QuickbooksDesktopQueue borderClass={borderClass} catalogs={catalogs} />

                        <QuickbooksDesktopConfigSetup />

                        <div className={"mt-5"}>
                            <div className={"mb-2 d-flex align-items-center"}>
                                <OwTitle title={"configure"} className={"me-2 ms-2"} tag={"h4"} />
                                <div>
                                    <OwButton fn={updateDefaults} title={"save"} active={updating} disableOnActive={true} />
                                </div>

                                {isDirty ? (
                                    <div className={"bg-danger rounded-5 ms-4 px-2 py-1"}>
                                        <OwLabel title={"unsaved_changes"} leftIconClass={"fad fa-exclamation-triangle"} />
                                    </div>
                                ) : null}
                            </div>

                            {uiContext.showHiddenFeatures ? (
                                <div className="mt-4">
                                    <div>
                                        <OwLabel title={"Queue"} isBold={true} />
                                    </div>
                                    <div>
                                        <OwToggle
                                            fn={handleChange}
                                            name={"qbd_use_queue_table"}
                                            label={"qbd_use_queue_table"}
                                            active={!!supplierIntegration.qbd_use_queue_table}
                                        />
                                    </div>
                                </div>
                            ) : null}

                            <QuickbooksDesktopConfigItemsSection
                                handleChange={handleChange}
                                borderClass={borderClass}
                                catalogs={catalogs}
                            />

                            <QuickbooksDesktopConfigCustomersSection
                                handleChange={handleChange}
                                borderClass={borderClass}
                                catalogs={catalogs}
                            />

                            <QuickbooksDesktopConfigOrdersSection
                                handleChange={handleChange}
                                borderClass={borderClass}
                                catalogs={catalogs}
                            />

                            <QuickbooksDesktopConfigSalesOrdersSection
                                handleChange={handleChange}
                                borderClass={borderClass}
                                catalogs={catalogs}
                            />

                            <QuickbooksDesktopConfigInvoicesSection
                                handleChange={handleChange}
                                borderClass={borderClass}
                                catalogs={catalogs}
                            />

                            <QuickbooksDesktopConfigOtherSection borderClass={borderClass} />
                        </div>
                    </div>
                ) : null}
            </OwAccordion>
        </div>
    );
};
